import { useState, useEffect, useRef } from 'react';

// Hook per ottimizzazione immagini con lazy loading e intersection observer
export const useImageOptimization = (src: string, options: {
  lazy?: boolean;
  placeholder?: string;
  onLoad?: () => void;
  onError?: () => void;
} = {}) => {
  const [imageSrc, setImageSrc] = useState(options.placeholder || '');
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (!options.lazy) {
      loadImage();
      return;
    }

    // Lazy loading con Intersection Observer
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              loadImage();
              observerRef.current?.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1, rootMargin: '50px' }
      );
    }

    if (imgRef.current) {
      observerRef.current.observe(imgRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [src, options.lazy]);

  const loadImage = () => {
    const img = new Image();
    img.src = src;

    img.onload = () => {
      setImageSrc(src);
      setIsLoaded(true);
      options.onLoad?.();
    };

    img.onerror = () => {
      setHasError(true);
      options.onError?.();
    };
  };

  return {
    imageSrc,
    isLoaded,
    hasError,
    imgRef
  };
};

// Hook per ottimizzazione bundle con preload intelligente
export const useBundleOptimization = () => {
  const preloadComponent = (importFn: () => Promise<any>) => {
    // Preload in background senza bloccare
    setTimeout(() => {
      importFn().catch(() => {
        // Ignore preload errors
      });
    }, 100);
  };

  const preloadRoute = (route: string) => {
    // Preload route basata su navigazione prevista
    const routeMap: Record<string, () => Promise<any>> = {
      '/search': () => import('../pages/PropertySearch'),
      '/profile': () => import('../pages/ProfilePage'),
      '/documents': () => import('../pages/DocumentsPage'),
      '/admin': () => import('../pages/AdminDashboard'),
    };

    const preloadFn = routeMap[route];
    if (preloadFn) {
      preloadComponent(preloadFn);
    }
  };

  return { preloadComponent, preloadRoute };
};

// Hook per ottimizzazione performance con throttling
export const useThrottle = <T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): T => {
  const lastCallRef = useRef<number>(0);

  return ((...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastCallRef.current >= delay) {
      lastCallRef.current = now;
      return callback(...args);
    }
  }) as T;
};

// Hook per ottimizzazione memoria con cleanup automatico
export const useMemoryOptimization = () => {
  const cleanupRefs = useRef<Set<() => void>>(new Set());

  const addCleanup = (cleanup: () => void) => {
    cleanupRefs.current.add(cleanup);
  };

  const removeCleanup = (cleanup: () => void) => {
    cleanupRefs.current.delete(cleanup);
  };

  useEffect(() => {
    return () => {
      cleanupRefs.current.forEach(cleanup => cleanup());
      cleanupRefs.current.clear();
    };
  }, []);

  return { addCleanup, removeCleanup };
};